import { useState } from 'react';
import { MessageContent } from './MessageContent';
import { Copy, ThumbsUp, ThumbsDown, FileText, Image, Download, User, Bot, Check, Pencil } from 'lucide-react';
import type { Message } from '../../types/chat';

interface ChatMessageProps {
  message: Message;
  isStreaming?: boolean;
  onEdit?: (messageId: string, content: string) => void;
  onLike?: (messageId: string) => void;
  onDislike?: (messageId: string) => void;
  onCopy?: (content: string) => void;
}

export function ChatMessage({ 
  message, 
  isStreaming = false, 
  onEdit, 
  onLike, 
  onDislike, 
  onCopy 
}: ChatMessageProps) {
  const isUser = message.sender === 'user';
  const isBot = message.sender === 'bot';
  const [showActions, setShowActions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // S'assurer que le contenu est toujours une chaîne valide
  // Gérer le cas où content est un array (données corrompues dans localStorage)
  const safeContent = (() => {
    const c = message.content;
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) {
      return c.filter((item): item is string => typeof item === 'string').join('');
    }
    if (c === null || c === undefined) return '';
    return String(c);
  })();
  const [editContent, setEditContent] = useState(safeContent);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(safeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.(safeContent);
    } catch (error) {
      console.error('Échec de la copie:', error);
    }
  };

  const handleSaveEdit = () => {
    onEdit?.(message.id, editContent);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditContent(safeContent);
    setIsEditing(false);
  };

  const handleDownloadAttachment = (attachment: Message['attachment']) => {
    if (!attachment) return;
    
    try {
      // Si c'est une URL Cloudinary (https://), ouvrir dans un nouvel onglet
      if (attachment.content.startsWith('http://') || attachment.content.startsWith('https://')) {
        window.open(attachment.content, '_blank');
      } else {
        // Fallback pour base64 ou blob (ancien format)
        const link = document.createElement('a');
        link.href = attachment.content;
        link.download = attachment.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Échec du téléchargement:', error);
    }
  };

  const getAttachmentIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const formatFileSize = (content: string) => {
    try {
      // Si c'est une URL, on ne peut pas calculer la taille depuis le contenu
      if (content.startsWith('http://') || content.startsWith('https://')) {
        return 'Fichier';
      }
      const size = Math.round((content.length * 3) / 4 / 1024);
      return size < 1024 ? `${size} Ko` : `${Math.round(size / 1024 * 10) / 10} Mo`;
    } catch {
      return '';
    }
  };

  return (
    <div 
      className={`group relative w-full ${isUser ? 'flex justify-end' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => !isEditing && setShowActions(false)}
    >
      {/* Layout style Notion - pas de bulles */}
      <div className={`flex items-start space-x-3 py-2 px-2 -mx-2 rounded-lg hover:bg-muted/30 transition-colors ${isUser ? 'max-w-[85%] flex-row-reverse space-x-reverse' : ''}`}>
        {/* Avatar */}
        <div className={`
          w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5
          ${isUser ? 'bg-primary/10' : 'bg-primary/10'}
        `}>
          {isUser ? (
            <User className="h-4 w-4 text-primary" />
          ) : (
            <Bot className="h-4 w-4 text-primary" />
          )}
        </div>

        {/* Contenu */}
        <div className="flex-1 min-w-0">
          {/* Header avec nom et timestamp */}
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-sm font-medium text-foreground">
              {isUser ? 'Vous' : 'ADHA'}
            </span>
            <span className="text-xs text-muted-foreground">
              {new Date(message.timestamp).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
            {isStreaming && (
              <span className="inline-flex items-center space-x-1.5 text-xs text-primary">
                <span className="w-1.5 h-1.5 bg-primary rounded-full animate-ping-slow" />
                <span className="animate-pulse-subtle">En cours...</span>
              </span>
            )}
          </div>

          {/* Message content - éditable style Notion */}
          {isEditing && isUser ? (
            <div className="space-y-2">
              <textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    handleSaveEdit();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancelEdit();
                  }
                }}
                autoFocus
                className="
                  w-full min-h-[60px] p-3 
                  bg-transparent border-0 border-l-2 border-primary/30
                  text-sm leading-relaxed
                  resize-none
                  focus:outline-none focus:border-primary
                  placeholder:text-muted-foreground/50
                "
                style={{ height: 'auto' }}
              />
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <span>⌘+Enter sauvegarder</span>
                <span>•</span>
                <span>Esc annuler</span>
                <div className="flex-1" />
                <button
                  onClick={handleCancelEdit}
                  className="px-2 py-1 hover:bg-muted rounded transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-2 py-1 bg-primary/10 text-primary hover:bg-primary/20 rounded transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-foreground leading-relaxed">
              <MessageContent 
                content={safeContent}
                isStreaming={isStreaming && isBot}
                onEdit={isBot && onEdit ? (newContent) => onEdit(message.id, newContent) : undefined}
              />
            </div>
          )}

          {/* Pièce jointe - style épuré */}
          {message.attachment && (
            <div className="mt-3 inline-flex items-center space-x-3 px-3 py-2 bg-muted/50 rounded-lg">
              <div className="p-2 bg-background rounded">
                {getAttachmentIcon(message.attachment.type)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate text-foreground">
                  {message.attachment.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(message.attachment.content)}
                </p>
              </div>
              <button
                onClick={() => handleDownloadAttachment(message.attachment)}
                className="p-1.5 hover:bg-muted rounded transition-colors text-muted-foreground hover:text-foreground"
                title="Télécharger"
              >
                <Download className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Actions flottantes style Notion */}
        <div className={`
          flex items-center space-x-0.5 transition-opacity duration-150
          ${showActions && !isStreaming ? 'opacity-100' : 'opacity-0'}
        `}>
          {/* Actions utilisateur */}
          {isUser && !isEditing && onEdit && (
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Copier */}
          <button
            onClick={handleCopy}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={copied ? 'Copié !' : 'Copier'}
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>

          {/* Actions bot uniquement */}
          {isBot && (
            <>
              <button
                onClick={() => onLike?.(message.id)}
                className={`p-1.5 rounded hover:bg-muted transition-colors ${
                  (message.likes ?? 0) > 0 ? 'text-green-500' : 'text-muted-foreground hover:text-foreground'
                }`}
                title="Utile"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => onDislike?.(message.id)}
                className={`p-1.5 rounded hover:bg-muted transition-colors ${
                  (message.dislikes ?? 0) > 0 ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'
                }`}
                title="Pas utile"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}